import { Context, createContext, createElement, Dispatch, ReactNode, Reducer, useContext } from 'react';

import { ProvideReducer } from '@/library/react/providers/provideReducerInner';

interface ReducerContextType<State, Action> {
  state: State;
  dispatch: Dispatch<Action>;
}

const createReducerContext = <State, Action>() =>
  createContext<ReducerContextType<State, Action>>({} as ReducerContextType<State, Action>);

export const provideReducer = <State, Action>({
  reducer,
  defaultState,
}: {
  reducer: Reducer<State, Action>;
  defaultState?: () => State;
}) => {
  const Context = createReducerContext<State, Action>();

  return {
    Provider: ({
      children,
      initialState,
      mountStrategy = 'afterPaint',
    }: {
      children: ReactNode;
      initialState?: () => Partial<State>;
      /** `immediate`: mount reducer on first paint (no one-frame null). Default defers until after mount (legacy). */
      mountStrategy?: 'afterPaint' | 'immediate';
    }) =>
      createElement(
        ProvideReducer<State, Action>,
        {
          Context: Context as Context<ReducerContextType<State, Action>>,
          reducer,
          defaultState,
          initialState,
          mountStrategy,
        },
        children
      ),
    useState: () => useContext(Context),
  };
};
